import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Edit3, Layers, FileSpreadsheet, Sparkles } from "lucide-react";

export default function Templates() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/templates");
      setItems(data);
    } catch {
      toast.error("Failed to load templates");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!window.confirm("Delete this template?")) return;
    try {
      await api.delete(`/templates/${id}`);
      toast.success("Deleted");
      load();
    } catch { toast.error("Failed"); }
  };

  return (
    <div className="space-y-6" data-testid="templates-page">
      <div className="flex items-end justify-between gap-4">
        <div>
          <div className="font-mono text-xs uppercase tracking-widest text-[#002FA7] font-bold mb-2">// Templates</div>
          <h1 className="font-display font-black text-4xl tracking-tighter">Your card designs.</h1>
        </div>
        <Link to="/templates/new" className="inline-flex items-center gap-2 bg-[#002FA7] text-white font-bold uppercase tracking-wider px-5 py-3 brutal-border brutal-shadow brutal-hover text-xs" data-testid="new-template">
          <Plus className="w-4 h-4"/> New Template
        </Link>
      </div>

      {loading ? (
        <div className="font-mono text-sm uppercase tracking-widest">Loading...</div>
      ) : items.length === 0 ? (
        <div className="bg-white brutal-border brutal-shadow p-12 text-center">
          <Layers className="w-12 h-12 mx-auto mb-4 text-[#002FA7]" strokeWidth={2}/>
          <h3 className="font-display font-bold text-2xl mb-2">No templates yet</h3>
          <p className="text-gray-600 mb-6">Upload your blank ID card and drop fields on it.</p>
          <Link to="/templates/new" className="inline-flex items-center gap-2 bg-black text-white font-bold uppercase tracking-wider px-5 py-3 brutal-border brutal-shadow brutal-hover text-xs">
            <Plus className="w-4 h-4"/> Create First Template
          </Link>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map(t => (
            <div key={t.id} className="bg-white brutal-border brutal-shadow flex flex-col" data-testid={`tpl-${t.id}`}>
              <div className="aspect-[16/10] bg-[#E5E7EB] pattern-checkerboard border-b-2 border-black overflow-hidden flex items-center justify-center">
                {t.image_path && (
                  <img src={fileUrl(t.image_path)} alt={t.name} className="max-h-full max-w-full object-contain"/>
                )}
              </div>
              <div className="p-4 flex-1">
                <h3 className="font-display font-bold text-xl mb-1">{t.name}</h3>
                <div className="font-mono text-xs text-gray-500">{t.fields?.length || 0} fields · {t.image_width}×{t.image_height}</div>
              </div>
              <div className="grid grid-cols-4 border-t-2 border-black">
                <Link to={`/generate/${t.id}`} className="p-3 hover:bg-[#002FA7] hover:text-white border-r-2 border-black flex flex-col items-center gap-1 font-mono text-[10px] uppercase tracking-widest font-bold" data-testid={`generate-${t.id}`}>
                  <Sparkles className="w-4 h-4"/> Generate
                </Link>
                <Link to={`/bulk/${t.id}`} className="p-3 hover:bg-[#22C55E] hover:text-black border-r-2 border-black flex flex-col items-center gap-1 font-mono text-[10px] uppercase tracking-widest font-bold" data-testid={`bulk-${t.id}`}>
                  <FileSpreadsheet className="w-4 h-4"/> Bulk
                </Link>
                <Link to={`/templates/${t.id}/edit`} className="p-3 hover:bg-gray-100 border-r-2 border-black flex flex-col items-center gap-1 font-mono text-[10px] uppercase tracking-widest font-bold" data-testid={`edit-${t.id}`}>
                  <Edit3 className="w-4 h-4"/> Edit
                </Link>
                <button onClick={()=>del(t.id)} className="p-3 hover:bg-[#FF3B30] hover:text-white flex flex-col items-center gap-1 font-mono text-[10px] uppercase tracking-widest font-bold" data-testid={`del-${t.id}`}>
                  <Trash2 className="w-4 h-4"/> Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
